import { AnimatePresence, motion } from 'motion/react'
import type { ReactNode } from 'react'
import stonePixelated from '../assets/stone_pixelated.png'
import { useI18n } from '../i18n/useI18n'
import { BottomNav } from './BottomNav'
import type { TabId } from './BottomNav'

type Props = {
  active: TabId
  onChange: (tab: TabId) => void
  badges?: Partial<Record<TabId, number>>
  dots?: Partial<Record<TabId, boolean>>
  headerRight?: ReactNode
  /** When true, bottom navigation is hidden (e.g. full-screen Account page). */
  hideBottomNav?: boolean
  /** Override motion transition key (defaults to `active`). */
  motionKey?: string
  children: ReactNode
}

export function Layout({
  active,
  onChange,
  badges,
  dots,
  headerRight,
  hideBottomNav = false,
  motionKey,
  children,
}: Props) {
  const { t } = useI18n()
  const transitionKey = motionKey ?? active

  return (
    <div className="relative mx-auto flex min-h-dvh max-w-[430px] bg-stone-850 flex-col overflow-hidden text-stone-100">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-cover bg-center brightness-40 opacity-20"
        style={{ backgroundImage: `url(${stonePixelated})` }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,0,0,0.08)_32%,rgba(0,0,0,0.85)_100%)]"
      />
      <div className="relative z-10 flex min-h-dvh flex-col">
        <header className="sticky top-0 z-30 border-b border-stone-800 bg-stone-950/95 px-4 py-3 backdrop-blur">
          <div className="flex items-baseline justify-between">
            <h1 className="text-sm font-bold uppercase tracking-[0.2em] text-stone-100">
              <img src="/favicon.svg" alt={t('layout_brand')} width={16} height={16} className="inline-block" /> {t('layout_brand')}
            </h1>
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wider text-stone-500">
                {t('layout_subtitle')}
              </span>
              {headerRight}
            </div>
          </div>
        </header>
        
        <main
          className={`relative flex-1 overflow-hidden px-4 pt-3 ${hideBottomNav ? 'pb-6' : 'pb-24'}`}
        >
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={transitionKey}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.18, ease: 'easeInOut' }}
            >
              {children}
            </motion.div>
          </AnimatePresence>
        </main>
        {!hideBottomNav && (
          <BottomNav active={active} onChange={onChange} badges={badges} dots={dots} />
        )}
      </div>
    </div>
  )
}
